import Vue from "vue";
import Vuex from "vuex";
import slugify from "slugify";
import get from "lodash/get";
import flatMap from "lodash/flatMap";
import uniqBy from "lodash/uniqBy";
import orderBy from "lodash/orderBy";
import intersectionBy from "lodash/intersectionBy";
import minBy from "lodash/minBy";
import cloneDeep from "lodash/cloneDeep";
import groupBy from "lodash/groupBy";

import SearchParamsDesktop from "../../components/Search/SearchParamsDesktop";
import SearchParamsMobile from "../../components/Search/SearchParamsMobile";
import Responsive from "../../components/core/Responsive";
import MobileFilters from "../../components/Search/MobileFilters/liveaboard";
import LiveaboardDiveNumberFilter from "../../components/Search/DesktopFilters/LiveaboardDiveNumberFilter";
import LiveaboardBudgetFilter from "../../components/Search/DesktopFilters/LiveaboardBudgetFilter";
import LiveaboardAvailabilityFilter from "../../components/Search/DesktopFilters/LiveaboardAvailabilityFilter";
import DestinationFilter from "../../components/Search/DesktopFilters/DestinationFilter";
import RatingFilter from "../../components/Search/DesktopFilters/RatingFilter";
import FacilitiesFilter from "../../components/Search/DesktopFilters/FacilitiesFilter";
import ExperiencesFilter from "../../components/Search/DesktopFilters/ExperiencesFilter";
import TripLengthFilter from "../../components/Search/DesktopFilters/TripLengthFilter";
import TagFilter from "../../components/Search/DesktopFilters/TagFilter";
import LiveaboardClearFiltersButton from "../../components/Search/LiveaboardClearFiltersButton";
import ZbModal from "../../components/core/ZbModal";
import LiveaboardResultPanel from "../../components/Search/LiveaboardResultPanel";
import LiveaboardResultSummary from "../../components/Search/LiveaboardResultSummary";
import SearchStickyNav from "../../components/Search/SearchStickyNav";
import {
  getPrimaryDestinationFilters,
  getSecondaryDestinationFilters,
  getRatingFilters,
  getObjectIDFromName,
  applyFilterURL,
  secondaryFilteredResults,
  groupItems,
} from "../../helpers/liveaboardSearchHelpers";
import {
  syncURLParams,
  searchWithDelay,
  allDestinationItems,
  getBudgetsFilter,
} from "../../helpers/searchHelpers";
import { SPECIES_NOT_VISIBLE } from "../../helpers/constants";
import once from "lodash/once";
import axios from "axios";
import moment from "moment";
import currencyRanges from "../../helpers/currencyRanges.json";

const algoliasearchV4 = require("algoliasearch");

Vue.filter("handleExternalURL", (url) => {
  if (url.startsWith("http")) {
    return url;
  } else {
    return `http://${url}`;
  }
});

Vue.component("search-params-desktop", SearchParamsDesktop);
Vue.component("search-params-mobile", SearchParamsMobile);
Vue.component("mobile-filters", MobileFilters);
Vue.component("responsive", Responsive);
Vue.component("liveaboard-budget-filter", LiveaboardBudgetFilter);
Vue.component("liveaboard-availability-filter", LiveaboardAvailabilityFilter);
Vue.component("liveaboard-dive-number-filter", LiveaboardDiveNumberFilter);
Vue.component("destination-filter", DestinationFilter);
Vue.component("rating-filter", RatingFilter);
Vue.component("facilities-filter", FacilitiesFilter);
Vue.component("experiences-filter", ExperiencesFilter);
Vue.component("trip-length-filter", TripLengthFilter);
Vue.component("liveaboard-clear-filters-button", LiveaboardClearFiltersButton);
Vue.component("liveaboard-result-summary", LiveaboardResultSummary);
Vue.component("tag-filter", TagFilter);

Vue.component("zb-modal", ZbModal);
Vue.component("result-panel", LiveaboardResultPanel);
Vue.component("search-sticky-nav", SearchStickyNav);

Vue.use({
  install(Vue) {
    const Algolia = algoliasearchV4(
      window.zubluAlgolia.applicationID,
      window.zubluAlgolia.publicAPIKey
    );
    const liveaboardIndex = Algolia.initIndex(
      window.zubluAlgolia.liveaboardIndex
    );
    const destinationIndex = Algolia.initIndex(
      window.zubluAlgolia.destinationIndex
    );

    Vue.prototype.$algolia = {
      instance: Algolia,
      searchAllLiveaboards: function ({ facetFilters, numericFilters }) {
        return new Promise((resolve, reject) => {
          liveaboardIndex
            .search("", {
              facetFilters,
              numericFilters,
              hitsPerPage: 1000,
            })
            .then(resolve)
            .catch(reject);
        });
      },
      searchDestinations: function ({ facetFilters }) {
        return new Promise((resolve, reject) => {
          destinationIndex
            .search("", {
              facetFilters,
              hitsPerPage: 1000,
            })
            .then(resolve)
            .catch(reject);
        });
      },
    };
  },
});

Vue.use(Vuex);
const AVAILABLE_RANKINGS = ["top-rated", "top-pick", "price", "special-offers"];
const AVAILABLE_VIEWINGS = ["boats", "dates"];

const plugin = (store) => {
  window.addEventListener("onCurrencyChanged", (e) => {
    store.commit("setCurrency", e.detail.currency);
    store.dispatch("loadStatistics");
    store.dispatch("loadSuppliers");
    applyFilterURL(store.state);
  });
};

const store = new Vuex.Store({
  state() {
    const searchParams = new URLSearchParams(window.location.search);

    let page = 1;
    if (searchParams.has("page") && searchParams.get("page") !== "1") {
      page = Number(searchParams.get("page"));

      if (Number.isNaN(page)) {
        page = 1;
      }
    }

    let view = "boats";
    if (
      searchParams.has("view") &&
      AVAILABLE_VIEWINGS.includes(searchParams.get("view"))
    ) {
      view = searchParams.get("view");
    }

    let ranking = "special-offers";
    if (
      searchParams.has("sort") &&
      AVAILABLE_RANKINGS.includes(searchParams.get("sort"))
    ) {
      ranking = searchParams.get("sort");
    } else if (view === "dates") {
      ranking = "price";
    }

    if (searchParams.has("budgets")) {
      const budgets = getBudgetsFilter("liveaboard");
      window.zubluSearchParams.budgets = budgets;
    }

    return {
      filters: window.zubluSearchParams,
      destination: window.zubluDestination,
      page,
      ranking: ranking,
      view: view,
      resortsResult: [],
      algoliaDestinations: [],
      statistics: null,
      searchType: window.zubluSearchType,
      from: searchParams.get("from"),
      to: searchParams.get("to"),
      guests: searchParams.get("guests"),
      isLoading: true,
      currency: window.topNavStore.state.currency,
    };
  },
  getters: {
    selectedMonth() {
      let selectedMonth = moment(
        get(window, ["topNavStore", "state", "formData", "dates", "0"], null)
      );

      if (selectedMonth.isValid() === false) {
        selectedMonth = moment().startOf("month");
      }

      return selectedMonth;
    },
    speciesVisibilityMatrix(state) {
      let result = {};

      for (const d of state.algoliaDestinations) {
        for (const s of d.__platform_species) {
          const speciesId =
            "parent" in s ? s.parent.id.toString() : s.id.toString();

          if (typeof result[speciesId] === "undefined") {
            result[speciesId] = {};
          }

          s.visibilities.forEach((value, month) => {
            if (!Array.isArray(result[speciesId][month + 1])) {
              result[speciesId][month + 1] = [];
            }

            if (value !== SPECIES_NOT_VISIBLE) {
              result[speciesId][month + 1].push(d);
            }
          });
        }
      }

      return result;
    },
    destinationsForSelectedSpecies(state, getters) {
      const month = getters.selectedMonth.month() + 1;
      const species = state.filters.species.split(",").filter((x) => x);

      let speciesDestinations = [];
      for (const speciesId of species) {
        if (speciesDestinations.length === 0) {
          speciesDestinations = get(
            getters.speciesVisibilityMatrix,
            [speciesId, month],
            []
          );
        } else {
          speciesDestinations = intersectionBy(
            speciesDestinations,
            get(getters.speciesVisibilityMatrix, [speciesId, month], []),
            (destination) => destination.objectID
          );
        }
      }

      speciesDestinations = uniqBy(speciesDestinations, (d) => d.objectID);

      return speciesDestinations;
    },
    limitedDestinationCodes(state) {
      const selectedDestinations = state.filters.destinations
        .split(",")
        .filter((item) => item);

      if (selectedDestinations.length) {
        return allDestinationItems
          .filter((dest) => {
            return selectedDestinations.includes(dest.id);
          })
          .map((dest) => dest.code);
      }

      if (state.destination.type === "destination") {
        return [state.destination.code];
      } else if (state.destination.type === "province") {
        return state.destination.children.map((d) => d.code);
      } else if (state.destination.type === "country") {
        return flatMap(
          state.destination.children,
          (province) => province.children
        ).map((d) => d.code);
      }

      return [];
    },
    showExperiencesFilter(state) {
      return (
        state.destination.type === "province" ||
        state.destination.type === "country"
      );
    },
    secondaryFilteredResults(state, getters) {
      return secondaryFilteredResults({
        suppliers: state.resortsResult,
        filters: state.filters,
        destinationsForSelectedSpecies: getters.destinationsForSelectedSpecies,
        experienceList: getters.experienceList,
        limitedDestinationCodes: getters.limitedDestinationCodes,
        currency: state.currency,
      });
    },
    sortedResults(state, getters) {
      let sorter = [];
      let sortdir = [];

      if (state.ranking === "top-rated") {
        sorter = [
          "__is_non_diving",
          (item) => get(item, ["__odyssey_zublu_rating"], 0),
          (item) => get(item, ["__odyssey_review_count"], 0),
          "__platform_tripadvisor_total_review",
        ];
        sortdir = ["asc", "desc", "desc", "desc"];
      }

      if (state.ranking === "price" || state.ranking === "special-offers") {
        if (state.ranking === "price") {
          sorter = [
            "__is_non_diving",
            "__cheapest_trip_price",
            "__platform_rating",
            "__platform_tripadvisor_total_review",
            "__odyssey_zublu_featured",
          ];
          sortdir = ["asc", "asc", "desc", "desc", "desc"];
        } else if (state.ranking === "special-offers") {
          sorter = [
            "__is_non_diving",
            "__has_special_offer",
            "__cheapest_trip_price",
            "__platform_rating",
            "__platform_tripadvisor_total_review",
            "__odyssey_zublu_featured",
          ];
          sortdir = ["asc", "desc", "asc", "desc", "desc", "desc"];
        }
      }

      return orderBy(getters.secondaryFilteredResults, sorter, sortdir);
    },
    experienceList(state) {
      let allSpecies = flatMap(
        state.algoliaDestinations,
        (dest) => dest.__platform_species
      );

      allSpecies = allSpecies.map((s) => {
        if (!("parent" in s)) {
          return s;
        }

        return s.parent;
      });
      allSpecies = uniqBy(allSpecies, (species) => species.id);
      allSpecies = orderBy(allSpecies, ["title"], ["asc"]);

      allSpecies.forEach((species) => {
        species.destinations = state.algoliaDestinations.filter(
          (destination) => {
            return destination.__platform_species.some((_species) => {
              return (
                _species.id === species.id ||
                get(_species, ["parent", "id"], null) === species.id
              );
            });
          }
        );
      });

      return allSpecies;
    },
    getValidTrips(state, getters) {
      return (trips) => {
        const tripLengths = state.filters.tripLengths
          .split(",")
          .filter((x) => x);

        const availability = state.filters.availability
          .split(",")
          .filter((x) => x);

        let result = [];
        if (trips) {
          result = trips
            .filter((trip) => {
              return (trip.destinations || []).some((destination) => {
                return getters.limitedDestinationCodes.includes(
                  destination.code
                );
              });
            })
            .filter((trip) => {
              if (getters.destinationsForSelectedSpecies.length === 0) {
                return true;
              }

              const destinationsInTrips = trip.destinations.map((d) => d.code);

              return getters.destinationsForSelectedSpecies.some((dest) => {
                return destinationsInTrips.includes(dest.__platform_skuKey);
              });
            })
            .filter((trip) => {
              if (tripLengths.length === 0) {
                return true;
              }

              if (tripLengths.includes("0")) {
                if (trip.length < 5) {
                  return true;
                }
              }

              if (tripLengths.includes("5")) {
                if (trip.length >= 5 && trip.length < 8) {
                  return true;
                }
              }

              if (tripLengths.includes("8")) {
                if (trip.length >= 8) {
                  return true;
                }
              }

              return false;
            })
            .filter((trip) => {
              let available = availability.includes("available-trips");
              let offers = availability.includes("special-offers");

              if (available && offers) {
                return trip.availability !== "Sold out" || !!trip.discount_tag;
              } else if (available) {
                return trip.availability !== "Sold out";
              } else if (offers) {
                return !!trip.discount_tag;
              }
              return true;
            });

          // Each trip has a date, an ID and an availability field
          // We need to filter out trips that has availability = sold out
          // but only if the whole month is sold out
          const groupedByMonth = groupBy(result, (trip) => {
            return moment(trip.date, "YYYY-MM-DD")
              .startOf("month")
              .format("YYYY-MM-DD");
          });

          for (const month in groupedByMonth) {
            const monthTrips = groupedByMonth[month];

            const isSoldOutFullMonth = monthTrips.every((trip) => {
              return trip.availability === "Sold out";
            });

            if (isSoldOutFullMonth) {
              const tripIdsToRemove = monthTrips.map((trip) => trip.id);
              // remove trips in monthTrips (idenfified by an id field)
              result = result.filter((trip) => {
                return !tripIdsToRemove.includes(trip.id);
              });
            }
          }
        }

        return orderBy(result, ["date"], ["asc"]);
      };
    },
    getTripsForSelectedMonth(state, getters) {
      return (trips) => {
        let monthToShow = moment(
          get(window, ["topNavStore", "state", "formData", "dates", "0"], null)
        );

        if (
          state.view === "dates" &&
          (!monthToShow || monthToShow.isValid() === false)
        ) {
          const tripsUnder30d = trips.filter((trip) => {
            const mmTripDate = moment(trip.date, "YYYY-MM-DD");

            return mmTripDate.diff(moment(), "days") <= 30;
          });

          return tripsUnder30d;
        }

        const validTrips = getters.getValidTrips(trips);

        if (monthToShow.isValid() === false) {
          const currentMonth = moment().startOf("month");
          const aTrip = validTrips.find((trip) => {
            const mmTrip = moment(trip.date, "YYYY-MM-DD").startOf("month");

            return mmTrip.isSameOrAfter(currentMonth, "month");
          });

          if (aTrip) {
            monthToShow = moment(aTrip.date, "YYYY-MM-DD");
          }
        }

        return validTrips.filter((trip) => {
          const mmTripDate = moment(trip.date, "YYYY-MM-DD");

          return (
            mmTripDate.month() === monthToShow.month() &&
            mmTripDate.year() === monthToShow.year()
          );
        });
      };
    },
    availableBudgetOptions(state) {
      return get(state.statistics, ["visibleBudgets"], {});
    },
    allTrips(state, getters) {
      let trips = [];
      getters.sortedResults.forEach((_r) => {
        let temp = _r.__odyssey_trips.map((_t) => {
          _t.platform_name = _r.__platform_name;
          _t.platform_url = _r.__platform_url;
          return _t;
        });
        trips = trips.concat(temp);
      });

      if (state.ranking === "price") {
        trips = orderBy(trips, ["price.price"], ["asc"]);
      }

      return trips;
    },
    tripsBydates(state, getters) {
      let groups = groupItems(getters.allTrips, "date");

      let groupArrays = Object.keys(groups).map((date) => {
        return {
          date,
          trips: groups[date],
        };
      });
      groupArrays = orderBy(groupArrays, ["date"], ["asc"]);

      return groupArrays;
    },
  },
  mutations: {
    setResult(state, resortsResult) {
      state.resortsResult = resortsResult;
    },
    setStatistics(state, statistics) {
      state.statistics = statistics;
    },
    setPage(state, page) {
      state.page = page;
      const searchParams = new URLSearchParams(window.location.search);

      if (state.page !== 1) {
        searchParams.set("page", state.page);
      } else {
        searchParams.delete("page");
      }

      syncURLParams(searchParams);
    },
    setRanking(state, ranking) {
      state.ranking = ranking;

      const searchParams = new URLSearchParams(window.location.search);
      if (state.ranking !== "top-rated") {
        searchParams.set("sort", state.ranking);
      } else {
        searchParams.delete("sort");
      }

      syncURLParams(searchParams);
    },
    setView(state, view) {
      state.view = view;

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("view", state.view);

      syncURLParams(searchParams);
    },
    setIsLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setAlgoliaDestinations(state, algoliaDestinations) {
      state.algoliaDestinations = algoliaDestinations;
    },
    setFilters(state, filters) {
      state.filters = filters;
      applyFilterURL(state);
    },
    setFilter(state, { name, value }) {
      state.filters[name] = value;
      applyFilterURL(state);
    },
    clearFilters(state) {
      state.filters = {
        budgets: "",
        destinations: "",
        rating: "",
        facilities: "",
        species: "",
        tripLengths: "",
        numDives: "",
        tags: "",
        availability: "",
      };
      state.page = 1;

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete("budgets");
      searchParams.delete("destinations");
      searchParams.delete("rating");
      searchParams.delete("facilities");
      searchParams.delete("species");
      searchParams.delete("tripLengths");
      searchParams.delete("page");
      searchParams.delete("numDives");
      searchParams.delete("goodFor");

      syncURLParams(searchParams);
    },
    setCurrency(state, value) {
      state.currency = value;
    },
  },
  actions: {
    loadSuppliers(context) {
      context.commit("setIsLoading", true);

      let primaryDestinationFilters = getPrimaryDestinationFilters(
        context.state.filters
      );

      if (typeof primaryDestinationFilters === "string") {
        primaryDestinationFilters = [
          primaryDestinationFilters,
          `__platform_searchable_destination_ids:${window.zubluDestination.id}`,
        ];
      } else if (Array.isArray(primaryDestinationFilters)) {
        primaryDestinationFilters.push(
          `__platform_searchable_destination_ids:${window.zubluDestination.id}`
        );
      }

      const facetFilters = [
        "__platform_type:liveaboard",
        primaryDestinationFilters,
        getSecondaryDestinationFilters(context.state.filters),
        getRatingFilters(context.state.filters),
      ].filter((f) => f);

      return searchWithDelay(
        this._vm.$algolia.searchAllLiveaboards,
        0
      )({ facetFilters })
        .then((resp) => {
          const result = resp.hits;
          const today = moment();

          for (const supplier of result) {
            if (supplier.__odyssey_trips) {
              supplier.__odyssey_trips = supplier.__odyssey_trips.filter(
                (trip) => {
                  const mmTripDate = moment(trip.date, "YYYY-MM-DD");

                  return mmTripDate.isSameOrAfter(today, "day");
                }
              );
            }

            supplier.__odyssey_trips = context.getters.getTripsForSelectedMonth(
              supplier.__odyssey_trips
            );

            const cheapestTrip = minBy(supplier.__odyssey_trips, (trip) => {
              return get(trip, ["price", "price"], 0);
            });

            if (!cheapestTrip) {
              supplier.__cheapest_trip_price = 0;
              supplier.__has_special_offer = 0;
              continue;
            }

            let lowestPrice = get(cheapestTrip, ["price", "price"], 0);
            const mmTrip = moment(cheapestTrip.date, "YYYY-MM-DD").startOf(
              "month"
            );
            if (mmTrip.isSame(today, "month") === false) {
              lowestPrice += 9999999; // to make it appear last
            }

            supplier.__cheapest_trip_price = lowestPrice;
            supplier.__has_special_offer = Number(
              supplier.__odyssey_trips.some((trip) => {
                return (
                  trip.discount_method === "custom" ||
                  (trip.discount_method !== "custom" &&
                    trip.discount_price !== 0 &&
                    trip.discount_price !== null)
                );
              })
            );
            supplier.__is_non_diving =
              supplier.__platform_search_tags.includes("non-diving-tour");
          }

          return result;
        })
        .then((result) => {
          result.forEach((item) => {
            item.inclusions_objects = (item.__odyssey_inclusions || [])
              .map((incl) => {
                return Object.assign(incl, {
                  slug: slugify(incl.name).toLowerCase(),
                });
              })
              .concat(
                (item.__odyssey_dive_facilities || []).map((incl) => {
                  return Object.assign(incl, {
                    slug: slugify(incl.name).toLowerCase(),
                  });
                })
              );
          });

          return result;
        })
        .then((result) => {
          const ids = result.map((item) => item.objectID);

          return window
            .fetchReviewsByIds({ supplier_ids: ids })
            .then(({ data }) => {
              if (data.length) {
                result.forEach((resort) => {
                  const rating = data.find(
                    (o) => +o.craft_id === +resort.objectID
                  );

                  if (rating) {
                    resort.rating = rating;
                    resort.__odyssey_zublu_rating = rating.zublu_rating;
                    resort.__odyssey_review_count = rating.count;
                  }
                });
              }
              return result;
            });
        })
        .then((result) => {
          context.commit("setResult", result);
          context.commit("setIsLoading", false);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    loadDestinations(context) {
      let facetFilters = "";

      if (window.zubluDestination.type === "country") {
        facetFilters = `__platform_country_id:${window.zubluDestination.id}`;
      } else if (window.zubluDestination.type === "province") {
        facetFilters = `__platform_region_id:${window.zubluDestination.id}`;
      }

      if (facetFilters) {
        return this._vm.$algolia
          .searchDestinations({ facetFilters })
          .then((result) => {
            context.commit("setAlgoliaDestinations", result.hits);

            return result.hits;
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },
    loadStatistics(context) {
      let primaryDestinationFilters = getPrimaryDestinationFilters(
        context.state.filters
      );

      if (typeof primaryDestinationFilters === "string") {
        primaryDestinationFilters = [
          primaryDestinationFilters,
          `__platform_searchable_destination_ids:${window.zubluDestination.id}`,
        ];
      } else if (Array.isArray(primaryDestinationFilters)) {
        primaryDestinationFilters.push(
          `__platform_searchable_destination_ids:${window.zubluDestination.id}`
        );
      }

      const facetFilters = [
        "__platform_type:liveaboard",
        primaryDestinationFilters,
      ].filter((f) => f);

      this._vm.$algolia
        .searchAllLiveaboards({
          facetFilters,
          numericFilters: "__odyssey_price_per_day > 0",
        })
        .then((resp) => {
          const result = resp.hits;
          const today = moment();

          for (const supplier of result) {
            if (supplier.__odyssey_trips) {
              supplier.__odyssey_trips = supplier.__odyssey_trips.filter(
                (trip) => {
                  const mmTripDate = moment(trip.date, "YYYY-MM-DD");

                  return mmTripDate.isSameOrAfter(today, "day");
                }
              );
            }
            supplier.__odyssey_trips = context.getters.getTripsForSelectedMonth(
              supplier.__odyssey_trips
            );
            supplier.__odyssey_trips = orderBy(
              supplier.__odyssey_trips,
              (trip) => {
                return get(trip, ["price", "price"], 0);
              }
            );
          }

          return result;
        })
        .then((results) => {
          const { state, getters } = context;
          const currentFilters = cloneDeep(state.filters);

          return secondaryFilteredResults({
            suppliers: results,
            filters: Object.assign(currentFilters, {
              budgets: "",
              rating: "",
              destinations: "",
            }),
            destinationsForSelectedSpecies:
              getters.destinationsForSelectedSpecies,
            experienceList: getters.experienceList,
            limitedDestinationCodes: getters.limitedDestinationCodes,
            currency: context.state.currency,
          });
        })
        .then((results) => {
          const visibleDestinations = results.reduce((agg, resort) => {
            for (const name of resort.__odyssey_destination_names) {
              const id = getObjectIDFromName(name);

              if (id) {
                agg[id] = true;
              }
            }

            for (const name of resort.__odyssey_regions) {
              const id = getObjectIDFromName(name);

              if (id) {
                agg[id] = true;
              }
            }

            return agg;
          }, {});

          const visibleRatings = results.reduce((agg, supplier) => {
            agg[supplier.__platform_rating] = true;

            return agg;
          }, {});

          const visibleBudgets = {
            min0: new Set(),
            min1000: new Set(),
            min2000: new Set(),
            min3000: new Set(),
            min4000: new Set(),
            min5000: new Set(),
          };

          const { currency } = context.state;
          const rangeInSelectedCurrency =
            currencyRanges[currency.code].liveaboard.filterRange;
          const rate = window.zubluCurrencyTable[currency.code];

          for (const supplier of results) {
            for (const trip of supplier.__odyssey_trips) {
              const price = get(trip, ["price", "price"], 0);

              if (price <= rangeInSelectedCurrency[0][1] * rate) {
                visibleBudgets.min0.add(supplier.objectID);
              } else if (price <= rangeInSelectedCurrency[1000][1] * rate) {
                visibleBudgets.min1000.add(supplier.objectID);
              } else if (price <= rangeInSelectedCurrency[2000][1] * rate) {
                visibleBudgets.min2000.add(supplier.objectID);
              } else if (price <= rangeInSelectedCurrency[3000][1] * rate) {
                visibleBudgets.min3000.add(supplier.objectID);
              } else if (price <= rangeInSelectedCurrency[4000][1] * rate) {
                visibleBudgets.min4000.add(supplier.objectID);
              } else {
                visibleBudgets.min5000.add(supplier.objectID);
              }
            }
          }

          visibleBudgets.min0 = visibleBudgets.min0.size;
          visibleBudgets.min1000 = visibleBudgets.min1000.size;
          visibleBudgets.min2000 = visibleBudgets.min2000.size;
          visibleBudgets.min3000 = visibleBudgets.min3000.size;
          visibleBudgets.min4000 = visibleBudgets.min4000.size;
          visibleBudgets.min5000 = visibleBudgets.min5000.size;

          context.commit("setStatistics", {
            visibleDestinations,
            visibleRatings,
            visibleBudgets,
          });
        });
    },
  },
  plugins: [plugin],
});

store.watch(
  (state, getters) => {
    return getters.sortedResults;
  },
  (sortedResults) => {
    const currentPage = store.state.page;
    const perPage = 12;
    const maxPages = Math.ceil(sortedResults.length / perPage);

    if (currentPage > maxPages && store.state.resortsResult.length) {
      store.commit("setPage", 1);
    }
  }
);

window.fetchReviewsByIds = once((body) => {
  return axios.post(
    `${window.ZUBLU_VARIABLES.ODYSSEY_API}/api/v1/reviews/queries`,
    body
  );
});

const apps = document.querySelectorAll(".vue-app");

Array.prototype.forEach.call(
  apps,
  (el) =>
    new Vue({
      el,
      template: el.innerHTML,
      store,
    })
);
